import { RECONCILIATION_QUERY_KEY } from '@features/reports/hooks/useReconciliation'
import { type Carer, UserRole } from '@lmc/ts-common'
import { Button, Heading, NavigationModal } from '@lmc/ui-kit'
import { Form as AntdForm, Divider, Form } from 'antd'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { capitalize } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import ModalLayout from 'screens/Rostering/components/ModalLayout'
import {
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY
} from 'screens/Rostering/hooks/queryConstants'
import useAllocationDoubleBookings from 'screens/Rostering/hooks/useAllocationDoubleBookings'
import { useGetPayRatesSettings } from 'screens/Rostering/hooks/usePaymentCards'
import type { UpdateRevisionPayload } from 'screens/Rostering/hooks/useRevisionUpdate'
import DoubleBookedModal from 'screens/Rostering/RotaBuilder/screens/AllocationWindow/components/DoubleBookedModal'
import {
  AboutShiftSection,
  AboutVisitSection,
  NeedsAssignedSection,
  SkillsRequiredSection
} from 'screens/Rostering/RotaPattern/sections'
import {
  type RotaEventInstance,
  RotaInstanceStatus
} from 'screens/Rostering/types'
import { styled } from 'styled-components'
import {
  buildTeamMemberRatesPayload,
  getRevisionReqFormInitialValues,
  getRevisionTeamMembersSource,
  getTeamMemberDisplayName,
  normalizeTeamMemberFormIds,
  type TeamMemberPayRateOverrideForm
} from '../utils'
import OverrideTeamMemberPayRatesSection from './OverrideTeamMemberPayRatesSection'

type EditReqModalProps = {
  editModalVisible: boolean
  onCancel: () => void
  revisionDetail: RotaEventInstance
  updateRevision?: (values: UpdateRevisionPayload) => Promise<unknown>
  isLoadingUpdate?: boolean
}

const EditReqModal = ({
  editModalVisible,
  onCancel,
  revisionDetail,
  updateRevision,
  isLoadingUpdate
}: EditReqModalProps) => {
  const [form] = AntdForm.useForm()
  const queryClient = useQueryClient()
  const sendEvent = useMixpanelEvent()

  const userRole = useSelector(
    (state: { global: { user: Carer } }) => state.global.user?.role
  )

  const isCareHomeAdmin =
    userRole === UserRole.careHomeAdmin || userRole === UserRole.management

  const initialValues = getRevisionReqFormInitialValues(revisionDetail)

  const originalTeamMemberIds = (
    getRevisionTeamMembersSource(revisionDetail) ?? []
  ).map((member) => member.teamMemberId)

  const [formData, setFormData] = useState<
    Record<string, unknown> | undefined
  >()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (editModalVisible) {
      // Include original team member IDs, clockIns, status, and id in formData for validation and filtering
      const formDataWithOriginals = {
        ...initialValues,
        originalTeamMemberIds,
        clockIns: revisionDetail?.clockIns,
        status: revisionDetail?.status,
        id: revisionDetail?.id,
        eventId: revisionDetail?.id
      }
      setFormData(formDataWithOriginals)
      form.setFieldsValue(initialValues)
    }
  }, [revisionDetail, editModalVisible])

  const [isDirty, setIsDirty] = useState(false)

  const [showPrompt, setShowPrompt] = useState(false)
  const [showDoubleBookedPrompt, setShowDoubleBookedPrompt] = useState(false)
  const [showStatusPrompt, setShowStatusPrompt] = useState(false)
  const [doubleBook, setDoubleBook] = useState(false)
  const [pendingUpdate, setPendingUpdate] =
    useState<UpdateRevisionPayload | null>(null)
  const [pendingOverrideEvents, setPendingOverrideEvents] = useState<
    Array<{ event: MixpanelEvents; properties: Record<string, unknown> }>
  >([])

  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: ['rota_admin_team_member_edit']
  })?.data

  const flagsRotaAdminTeamMemberEdit =
    flags?.rota_admin_team_member_edit?.enabled

  const isDone = revisionDetail?.status === RotaInstanceStatus.DONE

  const canEditByAdmin =
    flagsRotaAdminTeamMemberEdit && isCareHomeAdmin && isDone

  const { doubleBookings } = useAllocationDoubleBookings()

  const { data: payRatesSettings } = useGetPayRatesSettings()
  const payRatesEnabled = payRatesSettings?.enabled ?? false

  const isPastEventEnd = useMemo(() => {
    const end = revisionDetail?.endTime
    if (!end) {
      return false
    }
    return dayjs().isAfter(dayjs(end))
  }, [revisionDetail?.endTime])

  const handleCancel = () => {
    if (isDirty) {
      setShowPrompt(true)
      return
    }
    handleConfirmCancel()
  }

  const handleConfirmCancel = () => {
    onCancel()
    setFormData(undefined)
    form.setFieldsValue(initialValues)
  }

  const handleSave = async () => {
    if (isDirty && canEditByAdmin) {
      setShowStatusPrompt(true)
      return
    }

    if (isDirty) {
      await form.validateFields()
      form.submit()
      return
    }

    if (!isLoadingUpdate) handleConfirmCancel()
  }

  const onSave = async () => {
    if (isDirty) {
      await form.validateFields()
      form.submit()
      return
    }

    if (!isLoadingUpdate) handleConfirmCancel()
  }

  const handleFormValuesChange = (values: Record<string, unknown>) => {
    if (values) {
      setIsDirty(true)
    }

    setFormData((prev) => ({
      ...prev,
      ...values,
      // Preserve originalTeamMemberIds, clockIns, status, and id when form data changes
      originalTeamMemberIds:
        prev?.originalTeamMemberIds || originalTeamMemberIds,
      clockIns: prev?.clockIns ?? revisionDetail?.clockIns,
      status: prev?.status ?? revisionDetail?.status,
      id: prev?.id ?? revisionDetail?.id,
      eventId: prev?.eventId ?? revisionDetail?.id
    }))
  }

  const handleOnUpdateFinish = async (values: Record<string, unknown>) => {
    if (isDirty) {
      const {
        teamMembers,
        teamMemberPayRateOverrides: _omitted,
        ...otherValues
      } = values

      const initialValuesRecord: Record<string, unknown> = initialValues
      const changedValues = Object.entries(otherValues).reduce(
        (acc, [key, value]) => {
          const initialValue = initialValuesRecord[key]
          if (JSON.stringify(value) !== JSON.stringify(initialValue)) {
            acc[key] = value
          }
          return acc
        },
        {} as Record<string, unknown>
      )

      const updatePayload: UpdateRevisionPayload = {
        eventId: revisionDetail?.id,
        ...changedValues
      }

      const eventsToFire: Array<{
        event: MixpanelEvents
        properties: Record<string, unknown>
      }> = []

      if (payRatesEnabled && isCareHomeAdmin && !isPastEventEnd) {
        const assignedNow = normalizeTeamMemberFormIds(teamMembers)
        const ratesNow = buildTeamMemberRatesPayload(
          values.teamMemberPayRateOverrides as
            | Record<string, TeamMemberPayRateOverrideForm>
            | undefined,
          assignedNow
        )
        const assignedInitial = normalizeTeamMemberFormIds(
          initialValues.teamMembers
        )
        const ratesInitial = buildTeamMemberRatesPayload(
          initialValues.teamMemberPayRateOverrides,
          assignedInitial
        )
        if (JSON.stringify(ratesNow) !== JSON.stringify(ratesInitial)) {
          updatePayload.teamMemberRates = ratesNow

          type OverrideEntry = {
            overridePayRate: boolean
            customRate?: number
            rateType?: string
          }
          const currentOverrides =
            (values.teamMemberPayRateOverrides as Record<
              string,
              OverrideEntry
            >) ?? {}
          const initialOverrides =
            (initialValues.teamMemberPayRateOverrides as
              | Record<string, OverrideEntry>
              | undefined) ?? {}

          for (const [memberId, override] of Object.entries(currentOverrides)) {
            if (!override.overridePayRate) continue
            const prev = initialOverrides[memberId]
            const isNew = !prev || !prev.overridePayRate
            const isChanged =
              !isNew &&
              (override.customRate !== prev.customRate ||
                override.rateType !== prev.rateType)
            if (!isNew && !isChanged) continue

            eventsToFire.push({
              event: MixpanelEvents.PAY_RATE_OVERRIDE_ADDED,
              properties: {
                teamMember: getTeamMemberDisplayName(
                  memberId,
                  teamMembers,
                  getRevisionTeamMembersSource(revisionDetail)
                ),
                eventType: (
                  revisionDetail?.instanceType as unknown as
                    | { name?: string }
                    | undefined
                )?.name,
                dateOfEvent: revisionDetail?.startTime,
                customInput: override.customRate
              }
            })
          }
        }
      }

      let addedIds: string[] = []
      let removedIds: string[] = []
      let teamMembersDoubleBooked: string[] = []

      if (teamMembers) {
        const originalTeamMemberIds = (
          getRevisionTeamMembersSource(revisionDetail) ?? []
        ).map((member) => member.teamMemberId)

        const normalizedTeamMembers = Array.isArray(teamMembers)
          ? teamMembers.map((member) =>
              typeof member === 'object' ? member.value : member
            )
          : []

        addedIds = normalizedTeamMembers.filter(
          (id: string) => !originalTeamMemberIds.includes(id)
        )
        removedIds = originalTeamMemberIds.filter(
          (id: string) => !normalizedTeamMembers.includes(id)
        )

        // Check for double bookings if team members are being added
        if (addedIds.length > 0) {
          try {
            teamMembersDoubleBooked = await doubleBookings({
              id: revisionDetail?.id,
              teamMembers: addedIds
            })
          } catch {
            showToast({ title: t('SOMETHING_WENT_WRONG'), type: 'error' })
            return
          }

          // If there are double bookings and we haven't shown the prompt yet, show it
          if (!showDoubleBookedPrompt && teamMembersDoubleBooked?.length > 0) {
            updatePayload.teamMembers = {
              add: addedIds.map((id: string) => ({
                teamMemberId: id,
                role: 'carer'
              })),
              remove: removedIds,
              doubleBooked: teamMembersDoubleBooked,
              allowDoubleBooking: false
            }
            setPendingUpdate(updatePayload)
            setPendingOverrideEvents(eventsToFire)
            setShowDoubleBookedPrompt(true)
            return
          }
        }

        if (addedIds.length > 0 || removedIds.length > 0) {
          updatePayload.teamMembers = {
            add: addedIds.map((id: string) => ({
              teamMemberId: id,
              role: 'carer'
            })),
            remove: removedIds,
            doubleBooked: teamMembersDoubleBooked,
            allowDoubleBooking: doubleBook
          }
        }
      }

      if (revisionDetail?.siteShift && updatePayload.addressId) {
        updatePayload.addressId = undefined
      }

      const updateRes = await updateRevision?.(updatePayload)

      if (updateRes) {
        eventsToFire.forEach(({ event, properties }) => {
          sendEvent(event, properties)
        })
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY])
        setPendingUpdate(null)
        setShowDoubleBookedPrompt(false)
        setDoubleBook(false)
        handleConfirmCancel()
      }
    }
  }

  const handleDoubleBookedConfirm = async (allowDoubleBooking: boolean) => {
    setDoubleBook(allowDoubleBooking)

    if (pendingUpdate && pendingUpdate.teamMembers) {
      pendingUpdate.teamMembers.allowDoubleBooking = allowDoubleBooking

      const updateRes = await updateRevision?.(pendingUpdate)

      if (updateRes) {
        pendingOverrideEvents.forEach(({ event, properties }) => {
          sendEvent(event, properties)
        })
        setPendingOverrideEvents([])
        queryClient.invalidateQueries([REVISION_QUERY_KEY, revisionDetail?.id])
        queryClient.invalidateQueries([REVISIONS_QUERY_KEY])
        queryClient.invalidateQueries([RECONCILIATION_QUERY_KEY])
        setPendingUpdate(null)
        setShowDoubleBookedPrompt(false)
        setDoubleBook(false)
        handleConfirmCancel()
      }
    }
  }

  return (
    <ModalLayout
      open={editModalVisible}
      onCancel={handleCancel}
      headerHeight={8}
      footerContent={
        <FooterWrapper>
          <FooterRightSide>
            <Button onClick={handleCancel}>{t('CANCEL')}</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isLoadingUpdate}
            >
              {t('SAVE')}
            </Button>
          </FooterRightSide>
        </FooterWrapper>
      }
      headerContent={
        <>
          <StyledHeading level={2}>{t('EDIT_REQ')}</StyledHeading>
          <StyledDivider />
        </>
      }
    >
      <StyledForm
        form={form}
        onFinish={handleOnUpdateFinish}
        onValuesChange={handleFormValuesChange}
        initialValues={initialValues}
      >
        {!revisionDetail?.siteShift && (
          <NeedsAssignedSection formData={formData} />
        )}
        <SkillsRequiredSection formData={formData} />
        {revisionDetail?.siteShift ? (
          <AboutShiftSection
            hideAboutSection
            formData={formData}
            showTeamMembersSelect
          />
        ) : (
          <AboutVisitSection
            hideAboutSection
            formData={formData}
            showTeamMembersSelect
          />
        )}
        <OverrideTeamMemberPayRatesSection
          revisionDetail={revisionDetail}
          payRatesEnabled={payRatesEnabled && isCareHomeAdmin}
          payRateOverridesDisabled={isPastEventEnd}
        />
      </StyledForm>
      <NavigationModal
        showPrompt={showPrompt}
        modalTitle={t('ARE_YOU_SURE')}
        modalContentText={t('EXIT_EVENT')}
        modalConfirmText={t('EXIT')}
        modalCancelText={t('CANCEL')}
        confirmNavigation={() => {
          setShowPrompt(false)
          setIsDirty(false)
          handleConfirmCancel()
        }}
        cancelNavigation={() => setShowPrompt(false)}
      />
      {flagsRotaAdminTeamMemberEdit && (
        <NavigationModal
          showPrompt={showStatusPrompt}
          modalTitle={t('ARE_YOU_SURE_CONTINUE')}
          modalContentText={t('EDIT_EVENT_IN_STATUS', {
            eventStatus: capitalize(revisionDetail?.status)
          })}
          modalConfirmText={t('DONE', { defaultValue: 'Done' })}
          modalCancelText={t('CANCEL')}
          confirmNavigation={() => {
            setShowStatusPrompt(false)
            onSave()
          }}
          cancelNavigation={() => setShowStatusPrompt(false)}
          confirmButtonProps={{ variant: 'primary' }}
        />
      )}
      <DoubleBookedModal
        showPrompt={showDoubleBookedPrompt}
        setShowPrompt={setShowDoubleBookedPrompt}
        confirmNavigation={(allowDoubleBooking) =>
          handleDoubleBookedConfirm(allowDoubleBooking ?? false)
        }
        isDoubleBookedSelected={doubleBook}
        setIsDoubleBookedSelected={setDoubleBook}
        cancelNavigation={() => {
          setShowDoubleBookedPrompt(false)
          setPendingUpdate(null)
          setDoubleBook(false)
          // Reset form to initial values
          form.setFieldsValue(initialValues)
          setFormData(initialValues)
          setIsDirty(false)
        }}
      />
    </ModalLayout>
  )
}
const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 24px;
`
const StyledDivider = styled(Divider)`
  margin: 16px 0;
`
const FooterWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
const FooterRightSide = styled.div`
  display: flex;
  gap: 8px;
`
const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 10px;
` as unknown as typeof Form

export default EditReqModal
